/**
 * ProfileAvatar — mirrors apps/mobile/src/ui/Avatar.tsx's initials logic
 * (Arabic-aware, single initial for Arabic script). antd's Avatar already
 * falls back to children when the image errors, so no manual state needed.
 */
import { Avatar } from 'antd'
import type { AvatarSize } from 'antd/es/avatar/AvatarContext'

const ARABIC_MARKS = /[ً-ْٰـ]/gu
const ARABIC_SCRIPT = /\p{Script=Arabic}/u

export function initialsOf(name: string): string {
  const cleaned = name.replace(ARABIC_MARKS, '').trim()
  if (!cleaned) return ''

  const words = cleaned.split(/\s+/u).filter(word => /\p{L}/u.test(word))
  if (words.length === 0) return ''

  const first = Array.from(words[0] ?? '')[0] ?? ''
  if (ARABIC_SCRIPT.test(first)) return first

  const second = words.length > 1 ? (Array.from(words[words.length - 1] ?? '')[0] ?? '') : ''
  return `${first}${second}`.toUpperCase()
}

export interface ProfileAvatarProps {
  name: string
  url?: string | null
  size?: AvatarSize
}

export const ProfileAvatar = ({ name, url, size = 40 }: ProfileAvatarProps) => {
  const initials = initialsOf(name)
  return (
    <Avatar src={url ?? undefined} size={size} alt={name}>
      {initials}
    </Avatar>
  )
}
